<template>
  <div class="director-page">
    <div class="director-container">
      <div class="header-section">
        <h1>🎬 AI编导工作流 (6-Step Pipeline)</h1>
        <p>输入视频链接，按步骤完成从分析到画面重构的完整工作流</p>
      </div>

      <el-steps :active="currentStep" finish-status="success" align-center class="workflow-steps">
        <el-step title="步骤 1" description="解析与提取" />
        <el-step title="步骤 2" description="风格与剧情" />
        <el-step title="步骤 3" description="提炼角色" />
        <el-step title="步骤 4" description="角色图生成" />
        <el-step title="步骤 5" description="分镜脚本" />
        <el-step title="步骤 6" description="生分镜图" />
      </el-steps>

      <div class="step-content">
        <!-- Step 1: Parse and Load -->
        <div v-show="currentStep === 1" class="step-card">
          <h2>1. 视频解析与关键帧提取</h2>
          <div class="input-section">
            <HistoryInput
              ref="urlInputRef"
              v-model="videoUrl"
              :storageKey="'directorUrlHistory'"
              placeholder="请输入抖音、快手、小红书、B站等视频链接..."
              class="url-input"
              @enter="startParse"
            />
            <el-button type="primary" size="large" @click="startParse" :loading="parsing" class="action-btn">
              开始解析与提取
            </el-button>
          </div>
          
          <div v-if="parsing || taskId" class="progress-section">
            <el-progress :percentage="progress" :status="progressStatus" :stroke-width="15" striped striped-flow />
            <div class="progress-text">{{ progressMessage }}</div>
          </div>
          
          <div v-if="step1Completed" class="next-action">
            <el-button type="success" size="large" @click="goToStep(2)">进入下一步：风格分析</el-button>
          </div>
        </div>

        <!-- Step 2: Analysis -->
        <div v-show="currentStep === 2" class="step-card">
          <h2>2. 风格与剧情结构分析</h2>
          <div v-if="loadingStep2" class="loading-state" v-loading="true">正在分析视频风格特征...</div>
          <div v-else-if="step2Data">
            <el-descriptions border :column="1" direction="vertical">
              <el-descriptions-item label="视频整体风格">{{ step2Data.style }}</el-descriptions-item>
              <el-descriptions-item label="视觉特征">{{ step2Data.visual_style }}</el-descriptions-item>
              <el-descriptions-item label="剧情摘要">{{ step2Data.plot_summary }}</el-descriptions-item>
              <el-descriptions-item label="叙事结构">{{ step2Data.narrative_structure }}</el-descriptions-item>
            </el-descriptions>
            <div class="next-action">
              <el-button @click="goToStep(1)">上一步</el-button>
              <el-button type="success" @click="goToStep(3)">进入下一步：提炼角色</el-button>
            </div>
          </div>
        </div>

        <!-- Step 3: Character Prompts -->
        <div v-show="currentStep === 3" class="step-card">
          <h2>3. 提炼核心角色与提示词</h2>
          <div v-if="loadingStep3" class="loading-state" v-loading="true">正在提炼出场角色...</div>
          <div v-else-if="characters.length > 0">
            <el-row :gutter="20">
              <el-col :span="12" v-for="(char, index) in characters" :key="index" style="margin-bottom: 20px;">
                <el-card shadow="hover">
                  <template #header>
                    <div class="card-header">
                      <el-input v-model="char.name" style="max-width:200px; font-weight:bold;" size="small" />
                    </div>
                  </template>
                  <p><strong>原始描述：</strong> {{ char.description }}</p>
                  <div>
                    <strong>AI 生图提示词（请自由修改优化）：</strong>
                    <el-input v-model="char.image_prompt" type="textarea" :rows="4" style="margin-top: 10px;" />
                  </div>
                </el-card>
              </el-col>
            </el-row>
            <div class="next-action">
              <el-button @click="goToStep(2)">上一步</el-button>
              <el-button type="success" @click="goToStep(4)">进入下一步：生成角色图</el-button>
            </div>
          </div>
          <div v-else-if="!loadingStep3">没有提取到明显角色，可跳过。
            <div class="next-action">
              <el-button @click="goToStep(2)">上一步</el-button>
              <el-button type="success" @click="goToStep(4)">跳过下一步</el-button>
            </div>
          </div>
        </div>

        <!-- Step 4: Character Images -->
        <div v-show="currentStep === 4" class="step-card">
          <h2>4. 生成与确认角色立绘</h2>
          <el-row :gutter="20">
            <el-col :span="12" v-for="(char, index) in characters" :key="index" style="margin-bottom: 20px;">
              <el-card shadow="hover">
                <h3>{{ char.name }}</h3>
                <el-input v-model="char.image_prompt" type="textarea" :rows="3" style="margin-bottom:10px;" />
                <el-button type="primary" :loading="char.loadingImage" @click="generateCharacterImage(char)">
                  生图 / 重绘
                </el-button>
                <div v-if="char.imageUrl" style="margin-top:15px; text-align:center;">
                  <el-image :src="char.imageUrl" fit="contain" style="max-height: 300px; border-radius: 8px;" />
                </div>
              </el-card>
            </el-col>
          </el-row>
          <div class="next-action">
            <el-button @click="goToStep(3)">上一步</el-button>
            <el-button type="success" @click="goToStep(5)">角色确认完毕，下一步：分镜脚本</el-button>
          </div>
        </div>

        <!-- Step 5: Storyboard Script -->
        <div v-show="currentStep === 5" class="step-card">
          <h2>5. 智能生成分镜脚本</h2>
          <div v-if="loadingStep5" class="loading-state" v-loading="true">正在结合风格与角色重构分镜脚本...</div>
          <div v-else-if="storyboard.length > 0">
            <el-alert title="您可以自由编辑画面描述、台词和每镜的生图提示词" type="info" show-icon style="margin-bottom:20px;" />
            <div v-for="(scene, index) in storyboard" :key="index" class="scene-editor">
              <el-card shadow="hover" style="margin-bottom: 15px;">
                <template #header>
                  <div class="card-header">
                    <span style="font-weight:bold;">分镜 {{ index + 1 }}</span>
                    <el-input v-model="scene.shot_type" style="width: 120px; float:right;" size="small" placeholder="镜头类型" />
                  </div>
                </template>
                <el-form label-width="100px">
                  <el-form-item label="画面描述">
                    <el-input v-model="scene.description" type="textarea" :rows="2" />
                  </el-form-item>
                  <el-form-item label="台词/VO">
                    <el-input v-model="scene.dialogue" type="textarea" :rows="2" />
                  </el-form-item>
                  <el-form-item label="生图提示词">
                    <el-input v-model="scene.image_prompt" type="textarea" :rows="3" />
                  </el-form-item>
                </el-form>
              </el-card>
            </div>
            <div class="next-action">
              <el-button @click="goToStep(4)">上一步</el-button>
              <el-button type="success" @click="goToStep(6)">进入最后一步：生成分镜图</el-button>
            </div>
          </div>
        </div>

        <!-- Step 6: Storyboard Images -->
        <div v-show="currentStep === 6" class="step-card">
          <h2>6. 批量生成分镜图</h2>
          <div v-for="(scene, index) in storyboard" :key="index" class="scene-renderer" style="margin-bottom: 20px;">
            <el-card shadow="hover">
              <el-row :gutter="20">
                <el-col :span="10">
                  <h4>分镜 {{ index + 1 }} ({{ scene.shot_type || '中景' }})</h4>
                  <p class="scene-text"><strong>画面描述：</strong> {{ scene.description }}</p>
                  <p class="scene-text"><strong>台词：</strong> {{ scene.dialogue }}</p>
                  <el-input v-model="scene.image_prompt" type="textarea" :rows="4" style="margin-bottom: 10px;" />
                  <el-button type="primary" :loading="scene.loadingImage" @click="generateSceneImage(scene)">
                    在此提示词上生图 / 重绘
                  </el-button>
                </el-col>
                <el-col :span="14">
                  <div v-if="scene.imageUrl" class="image-preview">
                    <el-image :src="scene.imageUrl" fit="contain" style="width:100%; max-height: 400px; border-radius: 8px;" />
                  </div>
                  <div v-else class="empty-image">等待生图...</div>
                </el-col>
              </el-row>
            </el-card>
          </div>
          <div class="next-action">
            <el-button @click="goToStep(5)">上一步</el-button>
            <el-button type="success" size="large" @click="finishWorkflow">工作流完成</el-button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import HistoryInput from '@/components/HistoryInput.vue'

const API_BASE = ''

// State
const currentStep = ref(1)
const videoUrl = ref('')
const urlInputRef = ref<InstanceType<typeof HistoryInput>>()

// Step 1 State
const parsing = ref(false)
const taskId = ref('')
const progress = ref(0)
const progressMessage = ref('')
const error = ref('')
const step1Completed = ref(false)
let pollTimer: ReturnType<typeof setInterval> | null = null

// Step 2 State
const loadingStep2 = ref(false)
const step2Data = ref<any>(null)

// Step 3 & 4 State
const loadingStep3 = ref(false)
const characters = ref<any[]>([])

// Step 5 & 6 State
const loadingStep5 = ref(false)
const storyboard = ref<any[]>([])

const progressStatus = computed(() => {
  if (progress.value >= 100) return 'success'
  if (error.value) return 'exception'
  return undefined
})

const startParse = async () => {
  if (!videoUrl.value.trim()) {
    ElMessage.warning('请输入视频链接')
    return
  }
  if (urlInputRef.value) {
    urlInputRef.value.addToHistory(videoUrl.value.trim())
  }
  
  parsing.value = true
  progress.value = 0
  progressMessage.value = '正在提交分析任务...'
  error.value = ''
  step1Completed.value = false
  taskId.value = ''

  try {
    const response = await fetch(`${API_BASE}/api/director/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: videoUrl.value,
        extract_frames: true,
        frame_interval: 5,
        max_duration: 120
      })
    })

    if (!response.ok) throw new Error('任务提交失败')
    const data = await response.json()
    taskId.value = data.task_id
    progressMessage.value = '任务已提交，开始解析...'
    startPolling()
  } catch (e: any) {
    error.value = e.message || '分析失败'
    parsing.value = false
  }
}

const startPolling = () => {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = setInterval(async () => {
    try {
      if(!taskId.value) return;
      const response = await fetch(`${API_BASE}/api/director/status/${taskId.value}`)
      const data = await response.json()
      progress.value = Math.round((data.progress || 0) * 100)
      progressMessage.value = data.message || '处理中...'

      if (data.status === 'completed') {
        stopPolling()
        parsing.value = false
        step1Completed.value = true
        ElMessage.success('第1步完成，可进入下一步')
      } else if (data.status === 'failed') {
        stopPolling()
        error.value = data.message || '分析失败'
        parsing.value = false
      }
    } catch (e) {
      console.error('Polling error:', e)
    }
  }, 2000)
}

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

// Workflow Logic
const goToStep = async (step: number) => {
  if (step === 2 && !step2Data.value) {
    await fetchStep2()
  } else if (step === 3 && characters.value.length === 0) {
    await fetchStep3()
  } else if (step === 5 && storyboard.value.length === 0) {
    await fetchStep5()
  }
  currentStep.value = step
}

const fetchStep2 = async () => {
  loadingStep2.value = true
  try {
    const res = await fetch(`${API_BASE}/api/director/step2-analysis/${taskId.value}`, { method: 'POST' })
    const data = await res.json()
    if(res.ok && data.success) {
      step2Data.value = data.data
      ElMessage.success('风格分析完成')
    } else throw new Error(data.detail || '请求失败')
  } catch(e:any) {
    ElMessage.error(e.message || '加载第2步失败')
  } finally {
    loadingStep2.value = false
  }
}

const fetchStep3 = async () => {
  loadingStep3.value = true
  try {
    const res = await fetch(`${API_BASE}/api/director/character-prompts/${taskId.value}`, { method: 'POST' })
    const data = await res.json()
    if(res.ok && data.success) {
      characters.value = data.data.map((c: any) => ({ ...c, imageUrl: '', loadingImage: false }))
      ElMessage.success('角色提取完成')
    } else throw new Error(data.detail || '请求失败')
  } catch(e:any) {
    ElMessage.error(e.message || '加载第3步失败')
  } finally {
    loadingStep3.value = false
  }
}

const fetchStep5 = async () => {
  loadingStep5.value = true
  try {
    const res = await fetch(`${API_BASE}/api/director/storyboard-script/${taskId.value}`, { method: 'POST' })
    const data = await res.json()
    if(res.ok && data.success) {
      storyboard.value = data.data.map((s: any) => ({ ...s, imageUrl: '', loadingImage: false }))
      ElMessage.success('分镜脚本生成完成')
    } else throw new Error(data.detail || '请求失败')
  } catch(e:any) {
    ElMessage.error(e.message || '加载第5步失败')
  } finally {
    loadingStep5.value = false
  }
}

const generateCharacterImage = async (char: any) => {
  if (!char.image_prompt) {
    ElMessage.warning('提示词不能为空')
    return
  }
  char.loadingImage = true
  try {
    const res = await fetch(`${API_BASE}/api/director/generate-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: char.image_prompt })
    })
    const data = await res.json()
    if(res.ok && data.success && data.images.length > 0) {
      char.imageUrl = data.images[0].url
      ElMessage.success('生图成功')
    } else throw new Error(data.detail || '生图失败')
  } catch(e:any) {
    ElMessage.error(e.message || '生图失败')
  } finally {
    char.loadingImage = false
  }
}

const generateSceneImage = async (scene: any) => {
  if (!scene.image_prompt) {
    ElMessage.warning('提示词不能为空')
    return
  }
  scene.loadingImage = true
  try {
    const res = await fetch(`${API_BASE}/api/director/generate-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: scene.image_prompt })
    })
    const data = await res.json()
    if(res.ok && data.success && data.images.length > 0) {
      scene.imageUrl = data.images[0].url
      ElMessage.success('分镜图生成成功')
    } else throw new Error(data.detail || '生图失败')
  } catch(e:any) {
    ElMessage.error(e.message || '生图失败')
  } finally {
    scene.loadingImage = false
  }
}

const finishWorkflow = () => {
  ElMessage.success('太棒了！AI编导工作流已完成，文件已整理完毕！')
}

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.director-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 40px 20px;
}
.director-container {
  max-width: 1000px;
  margin: 0 auto;
}
.header-section {
  text-align: center;
  margin-bottom: 40px;
  color: #333;
}
.workflow-steps {
  margin-bottom: 30px;
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.step-card {
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  min-height: 400px;
}
.input-section {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 30px;
}
.url-input {
  flex: 1;
  max-width: 600px;
}
.action-btn {
  white-space: nowrap;
}
.progress-section {
  max-width: 600px;
  margin: 0 auto;
}
.progress-text {
  margin-top: 15px;
  text-align: center;
  color: #666;
}
.next-action {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 40px;
}
.loading-state {
  text-align: center;
  padding: 60px 0;
  color: #909399;
}
h2 {
  margin-top: 0;
  margin-bottom: 25px;
  font-size: 20px;
  color: #303133;
  border-left: 4px solid #409EFF;
  padding-left: 10px;
}
.scene-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
  line-height: 1.5;
}
.empty-image {
  height: 300px;
  background: #f0f2f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 14px;
}
</style>
